/**
 * O validador.
 *
 * Câmera lendo QR e EAN-13, lanterna para portaria escura, e teclado para
 * digitar o código quando o ingresso está amassado ou a tela do cliente não lê.
 * Toda leitura passa pela mesma cadeia de regras — não há caminho alternativo
 * que valide sem checar.
 *
 * No computador quem lê é o leitor USB, não a câmera — como o motor do
 * terminal Gertec no app: não há imagem para mostrar nem mira para apontar, e
 * a tela vira totem. A fonte muda, a cadeia de regras não — as duas entram
 * pelo mesmo `receberCodigo`.
 *
 * O desfecho ocupa a tela inteira e fala por três canais ao mesmo tempo: cor,
 * ícone e palavra, mais som e vibração. Um operador de costas para o aparelho
 * precisa saber pelo bipe se a pessoa entrou.
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  Animated,
  AppState,
  FlatList,
  Modal,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import {SafeAreaView, useSafeAreaInsets} from 'react-native-safe-area-context';
import {
  ArrowLeft,
  CameraOff,
  ChevronRight,
  Flashlight,
  Info,
  Keyboard,
  ScanLine,
  Search,
  X,
  Zap,
} from 'lucide-react-native';
import {useIsFocused} from '@react-navigation/native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import {caixaAberto, definirMaosLivres, travarSetores, type Caixa} from '../caixa';
import {
  historicoDoAcesso,
  repositorioDoEvento,
  resumoDoEvento,
  type EntradaDoAcesso,
  type ResumoEvento,
} from '../dados/repositorio';
import type {ResultadoValidacao} from '../dominio/tipos';
import {validarIngresso} from '../dominio/validar';
import {LeitorCamera} from '../leitura/camera';
import {MODO_TERMINAL, useLeitorUsb} from '../leitura/leitorUsb';
import {carregarSessao} from '../sessao';
import {ehEventoDeDemonstracao} from '../dominio/demonstracao';
import {iniciarAgendador, type Agendador} from '../sync/agendador';
import {comMilhar, horaDe} from '../ui/datas';
import {txt, txtDoDominio, useIdioma} from '../ui/idioma';
import {IconeDoDesfecho} from '../ui/icones';
import {IconeLeitor} from '../ui/IconeLeitor';
import {marcarMaosLivres} from '../ui/maosLivres';
import {avisar} from '../ui/retorno';
import {manterTelaAcesa} from '../ui/tela';
import {Teclado} from '../ui/Teclado';
import {estilos, fontes, LARGURA_DA_COLUNA, useTema} from '../ui/tema';
import {Versao} from '../ui/Versao';
import type {ParamsPilha} from '../navegacao';

type Props = NativeStackScreenProps<ParamsPilha, 'Validador'>;

/** Tempo que o mesmo código é ignorado depois de lido — a câmera lê em rajada. */
const REPETICAO_MS = 2500;

/**
 * Quantas entradas o histórico traz por vez. Dez cobre a pergunta que aparece
 * na portaria — "acabei de passar, e agora?" — sem varrer o evento inteiro num
 * aparelho que também está lendo câmera.
 */
const PAGINA_HISTORICO = 10;

/**
 * Modo mãos livres.
 *
 * A leitura nunca para por causa de um desfecho automático: o resultado vira
 * uma faixa por cima da câmera, com som e vibração, e a próxima pessoa já pode
 * encostar o ingresso. Só um caso interrompe — o nominal, que a cadeia de
 * regras manda um humano conferir.
 */

/** Faixa do aprovado. Curta de propósito: a fila não pode esperar a tela. */
const FAIXA_APROVADO_MS = 3000;

/** Faixa da recusa. Mais longa: o operador precisa ler o motivo. */
const FAIXA_RECUSA_MS = 10000;

/**
 * Depois de uma recusa a leitura trava. Sem isso o próximo da fila encosta o
 * ingresso e bipa aprovado enquanto o operador ainda está barrando o anterior —
 * e os dois bipes viram um só na cabeça de quem está ouvindo.
 */
const TRAVA_APOS_RECUSA_MS = 2500;

/** Ingresso parado na frente da lente: ignorado em silêncio, sem re-bipar. */
const REPETICAO_MAOS_LIVRES_MS = 3000;

/** Toque longo para sair — com o aparelho na mão, toque curto sai sem querer. */
const TOQUE_PARA_SAIR_MS = 600;

/**
 * O número do lote para a tela.
 *
 * `loteStr` é herdado do TMobi e vem como "123 - 4123": o número do lote e a
 * forma preenchida com "4" que aparece dentro do código de barras. Só o
 * primeiro diz alguma coisa para quem está na portaria.
 */
function numeroDoLote(loteStr?: string): string | undefined {
  const numero = loteStr?.split('-')[0].trim();
  return numero ? numero : undefined;
}

/**
 * Setor, lote e número do ingresso, venha o dado de onde vier.
 *
 * O cadastro online só existe para ingresso nominal; o cortesia impresso e o
 * vendido em bilheteria chegam sem ele. O setor do lote — que sai do próprio
 * código de barras — é o que sobra nesses casos, e é por isso que a tela ficava
 * sem setor nenhum antes.
 */
function identificacao(resultado: ResultadoValidacao) {
  return {
    setor: resultado.online?.setor || resultado.setor?.nome || resultado.lote?.setor,
    lote: numeroDoLote(resultado.lote?.loteStr),
    /** Camarote, mesa, cadeira: é o que o operador fala em voz alta. */
    numero: resultado.online?.numero?.trim() || undefined,
  };
}

export function TelaValidador({navigation, route}: Props) {
  const {eventoId, eventoNome, codbarInicial} = route.params;
  const t = useTema();
  useIdioma();
  const {width: largura, height: altura} = useWindowDimensions();
  /**
   * A tela de leitura está na frente. Ir para a busca de ingressos não
   * desmonta esta tela — ela fica na pilha, e sem isto a câmera continuaria
   * filmando por baixo da outra: bateria gasta e luz de câmera em uso acesa
   * numa tela que não lê nada.
   */
  const naFrente = useIsFocused();

  /** O que a câmera do navegador contou de si: se já mostra imagem, se tem LED. */
  const [camera, setCamera] = useState({pronta: false, temLanterna: false});

  const [lanterna, setLanterna] = useState(false);
  /**
   * Câmera ligada pelo operador. Desligada, ela é solta de verdade — some a
   * luz de câmera em uso e o gasto de bateria de um sensor filmando a mesa.
   * Portaria parada entre uma leva e outra é o caso: liga quando alguém chega.
   * Não fica lembrada: voltar à tela é voltar para ler.
   */
  const [cameraLigada, setCameraLigada] = useState(true);
  const [digitando, setDigitando] = useState(false);
  const [digitado, setDigitado] = useState('');
  const [resultado, setResultado] = useState<ResultadoValidacao | null>(null);
  const [processando, setProcessando] = useState(false);
  const [resumo, setResumo] = useState<ResumoEvento | null>(null);
  const [caixa, setCaixa] = useState<Caixa | null>(null);
  const [emPrimeiroPlano, setEmPrimeiroPlano] = useState(true);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const [historico, setHistorico] = useState<EntradaDoAcesso[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [temMaisHistorico, setTemMaisHistorico] = useState(false);
  const [maosLivres, setMaosLivres] = useState(false);
  const [pedindoMaosLivres, setPedindoMaosLivres] = useState(false);
  /** Desfecho mostrado como faixa sobre a câmera, sem parar a leitura. */
  const [faixa, setFaixa] = useState<ResultadoValidacao | null>(null);

  /** Instante até o qual a leitura fica travada depois de uma recusa. */
  const travadoAte = useRef(0);
  const faixaProgresso = useRef(new Animated.Value(1)).current;

  const ultimaLeitura = useRef<{codbar: string; quando: number}>({codbar: '', quando: 0});
  const repositorio = useRef(repositorioDoEvento());
  const agendadorRef = useRef<Agendador | null>(null);
  const contagemRegressiva = useRef(new Animated.Value(1)).current;

  const atualizarResumo = useCallback(async () => {
    setResumo(await resumoDoEvento(eventoId));
  }, [eventoId]);

  /**
   * Uma página do histórico. `offset` zero recomeça a lista — é o que acontece
   * ao abrir; qualquer outro valor emenda no fim, que é o "carregar mais".
   */
  const carregarHistorico = useCallback(
    async (offset: number) => {
      setCarregandoHistorico(true);
      try {
        const pagina = await historicoDoAcesso(eventoId, PAGINA_HISTORICO, offset);
        setHistorico(atual => (offset === 0 ? pagina : [...atual, ...pagina]));
        // Página cheia é o sinal de que pode haver mais; página curta é o fim.
        setTemMaisHistorico(pagina.length === PAGINA_HISTORICO);
      } finally {
        setCarregandoHistorico(false);
      }
    },
    [eventoId],
  );

  const abrirHistorico = useCallback(() => {
    setVendoHistorico(true);
    carregarHistorico(0);
  }, [carregarHistorico]);

  /**
   * A volta é sempre para os setores, nunca para a tela anterior.
   *
   * Quem chega aqui pela busca de ingressos deixa a lista embaixo na pilha, e
   * um `goBack` devolveria o operador para ela no meio da fila. `popTo` porque
   * o `navigate` do React Navigation 7 não volta — empilharia um segundo
   * Setores por cima deste validador.
   */
  const voltarAosSetores = useCallback(() => {
    navigation.popTo('Setores', {eventoId, eventoNome});
  }, [navigation, eventoId, eventoNome]);

  // O app nativo prende aqui o botão voltar do Android. No navegador não há
  // botão do aparelho para prender — o react-native-web só acusa erro —, e a
  // seta da tela já leva aos setores.

  useEffect(() => {
    atualizarResumo();
    // Chegar aqui é a portaria abrindo: os setores desta portaria ficam
    // travados a partir de agora.
    travarSetores().then(() =>
      caixaAberto().then(aberto => {
        setCaixa(aberto);
        // O mãos livres é escolha do turno: reiniciou o aparelho, volta ligado.
        setMaosLivres(Boolean(aberto?.maosLivres));
      }),
    );
  }, [atualizarResumo]);

  // A tela só fica presa acesa enquanto o modo está ligado, e solta ao sair da
  // tela — aparelho no bolso com a tela acesa é bateria embora. O aviso de
  // versão nova também se cala enquanto o modo durar: ver src/ui/maosLivres.ts.
  useEffect(() => {
    manterTelaAcesa(maosLivres);
    marcarMaosLivres(maosLivres);
    return () => {
      manterTelaAcesa(false);
      marcarMaosLivres(false);
    };
  }, [maosLivres]);

  const ligarMaosLivres = useCallback((ligado: boolean) => {
    setPedindoMaosLivres(false);
    setMaosLivres(ligado);
    // Mãos livres é a câmera lendo sem toque: sem ela, o modo não existe.
    if (ligado) {
      setCameraLigada(true);
    }
    setFaixa(null);
    travadoAte.current = 0;
    definirMaosLivres(ligado);
  }, []);

  // Câmera desligada quando o app sai de frente — bateria de portaria é preciosa.
  useEffect(() => {
    const inscricao = AppState.addEventListener('change', estado =>
      setEmPrimeiroPlano(estado === 'active'),
    );
    return () => inscricao.remove();
  }, []);

  // O agendador cuida de quando subir: após validar, a cada minuto, quando a
  // rede volta e quando o app sai/volta da frente.
  useEffect(() => {
    let parar = () => {};

    (async () => {
      const sessao = await carregarSessao();
      if (!sessao) {
        return;
      }
      // O agendador não existe em demonstração, e este é o portão que protege
      // a contagem de público de um evento real: uma entrada de exemplo
      // enviada como válida estragaria o número de alguém, e ninguém
      // desconfiaria da origem. Ver src/dominio/demonstracao.ts.
      if (ehEventoDeDemonstracao(eventoId)) {
        return;
      }
      const agendador = iniciarAgendador({
        eventoId,
        machineId: sessao.machineId,
        aoSincronizar: () => atualizarResumo(),
      });
      agendadorRef.current = agendador;
      parar = () => {
        agendador.parar();
        agendadorRef.current = null;
      };
    })();

    return () => parar();
  }, [eventoId, atualizarResumo]);

  const validar = useCallback(
    async (codigo: string, dadosConfirmados = false) => {
      if (!codigo.trim() || processando) {
        return;
      }
      setProcessando(true);
      try {
        const r = await validarIngresso(codigo, eventoId, repositorio.current, {
          dadosConfirmados,
        });

        // No mãos livres o desfecho vira faixa e a câmera segue lendo por
        // baixo. A exceção é o nominal: a cadeia pede um humano, então a tela
        // cheia aparece e o modo fica pausado até o operador decidir.
        const semToque = maosLivres && r.motivo !== 'AGUARDANDO_CONFIRMACAO_DADOS';
        if (semToque) {
          setFaixa(r);
          if (!r.valido) {
            travadoAte.current = Date.now() + TRAVA_APOS_RECUSA_MS;
          }
        } else {
          setResultado(r);
        }

        avisar(r.motivo);
        if (r.motivo !== 'AGUARDANDO_CONFIRMACAO_DADOS') {
          await atualizarResumo();
        }
        if (r.valido) {
          // Entrou: manda subir. O agendador agrupa a rajada de leituras.
          agendadorRef.current?.pedirEnvio();
        }
      } finally {
        setProcessando(false);
      }
    },
    [eventoId, processando, atualizarResumo, maosLivres],
  );

  // Chegou de "Validar este ingresso" na busca ao portador.
  useEffect(() => {
    if (codbarInicial) {
      validar(codbarInicial);
    }
    // Só na entrada da tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codbarInicial]);

  // Todo desfecho devolve a câmera sozinho: três segundos para quem entrou, dez
  // para a recusa — que ainda oferece o botão para o operador encurtar. O
  // relógio da última leitura reinicia junto: senão o mesmo ingresso ainda na
  // frente da lente seria lido de novo assim que a tela sumisse.
  useEffect(() => {
    // A confirmação de dados tem relógio próprio, na tela dela — aqui só entram
    // os desfechos que o `TelaDesfecho` desenha, aprovados e recusados.
    if (!resultado || resultado.motivo === 'AGUARDANDO_CONFIRMACAO_DADOS') {
      return;
    }

    // A barra é a única pista de quanto tempo ainda resta antes de a câmera
    // voltar — sem ela o operador não sabe se espera ou se toca na tela.
    contagemRegressiva.setValue(1);
    Animated.timing(contagemRegressiva, {
      toValue: 0,
      duration: resultado.timeoutMs,
      useNativeDriver: false,
    }).start();

    const relogio = setTimeout(() => {
      ultimaLeitura.current = {codbar: resultado.codbar, quando: Date.now()};
      setResultado(null);
    }, resultado.timeoutMs);

    return () => clearTimeout(relogio);
  }, [resultado, contagemRegressiva]);

  // A faixa do mãos livres se apaga sozinha. Aprovado sai rápido porque a fila
  // anda; recusa fica o dobro, que é o tempo de o operador ler e barrar.
  useEffect(() => {
    if (!faixa) {
      return;
    }

    const duracao = faixa.valido ? FAIXA_APROVADO_MS : FAIXA_RECUSA_MS;
    faixaProgresso.setValue(1);
    Animated.timing(faixaProgresso, {
      toValue: 0,
      duration: duracao,
      useNativeDriver: false,
    }).start();

    const relogio = setTimeout(() => setFaixa(null), duracao);
    return () => clearTimeout(relogio);
  }, [faixa, faixaProgresso]);

  /**
   * A porta única por onde entra todo código lido, venha da câmera ou do
   * leitor USB.
   *
   * As duas fontes leem de jeitos muito diferentes e chegam aqui iguais: as
   * mesmas travas, a mesma janela de repetição, a mesma cadeia de regras. É o
   * que garante que o leitor não tenha um caminho de validação só dele.
   */
  const receberCodigo = useCallback(
    (valor: string, origem: string) => {
      if (!valor || resultado || processando || digitando) {
        return;
      }

      // O conteúdo cru do QR muda de ticketeira para ticketeira; registrar
      // ajuda a entender recusa estranha em campo sem precisar de aparelho.
      console.log(`[leitura] ${origem}: ${valor}`);
      const agora = Date.now();

      // Trava depois da recusa: o próximo da fila espera o operador terminar.
      if (agora < travadoAte.current) {
        return;
      }

      const janela = maosLivres ? REPETICAO_MAOS_LIVRES_MS : REPETICAO_MS;
      const ultima = ultimaLeitura.current;
      if (ultima.codbar === valor && agora - ultima.quando < janela) {
        return;
      }
      ultimaLeitura.current = {codbar: valor, quando: agora};
      validar(valor);
    },
    [resultado, processando, digitando, maosLivres, validar],
  );

  /**
   * Quando a portaria pode ler — vale para as duas fontes.
   *
   * A leitura para enquanto o histórico está aberto, senão um ingresso na
   * frente da lente seria validado com o operador de olho na lista. Mesma
   * coisa para desfecho na tela e teclado aberto.
   *
   * Com a página fora de frente não há leitura: a câmera está solta, e o
   * teclado do leitor USB está digitando em outra janela.
   */
  const leituraLiberada = emPrimeiroPlano && !resultado && !digitando && !vendoHistorico;

  // No computador a câmera nem sobe: quem lê é o leitor USB.
  const camaraAtiva = leituraLiberada && !MODO_TERMINAL && cameraLigada && naFrente;

  /** O botão e a tela de câmera desligada: só no celular, e fora do mãos livres. */
  const podeDesligarCamera = !MODO_TERMINAL && !maosLivres;

  // Escuta o leitor também no celular: leitor Bluetooth pareado chega igual, e
  // a câmera segue funcionando ao lado.
  useLeitorUsb(
    leituraLiberada,
    useCallback((codigo: string) => receberCodigo(codigo, 'leitor'), [receberCodigo]),
  );
  const confirmando = resultado?.motivo === 'AGUARDANDO_CONFIRMACAO_DADOS';

  /** Mira: quadrado central com cantos de marca e faixa de leitura no meio. */
  const lado = Math.min(largura * 0.71, altura * 0.4);
  const esquerda = (largura - lado) / 2;
  const topo = altura * 0.3;
  const canto = 44;
  const escuro = t.escurecedorMira;

  function mira() {
    const bracoDeCanto = (cima: boolean, esq: boolean) => ({
      position: 'absolute' as const,
      width: canto,
      height: canto,
      left: esq ? esquerda : esquerda + lado - canto,
      top: cima ? topo : topo + lado - canto,
      borderTopWidth: cima ? 4 : 0,
      borderBottomWidth: cima ? 0 : 4,
      borderLeftWidth: esq ? 4 : 0,
      borderRightWidth: esq ? 0 : 4,
      borderColor: t.primaria,
      borderTopLeftRadius: cima && esq ? 26 : 0,
      borderTopRightRadius: cima && !esq ? 26 : 0,
      borderBottomLeftRadius: !cima && esq ? 26 : 0,
      borderBottomRightRadius: !cima && !esq ? 26 : 0,
    });

    return (
      <>
        {/* Escurece tudo menos a janela de leitura, em quatro faixas: a sombra
            com espalhamento gigante do design não existe no React Native. */}
        <View style={{position: 'absolute', left: 0, right: 0, top: 0, height: topo, backgroundColor: escuro}} />
        <View style={{position: 'absolute', left: 0, right: 0, top: topo + lado, bottom: 0, backgroundColor: escuro}} />
        <View style={{position: 'absolute', left: 0, width: esquerda, top: topo, height: lado, backgroundColor: escuro}} />
        <View style={{position: 'absolute', right: 0, width: esquerda, top: topo, height: lado, backgroundColor: escuro}} />

        <View style={bracoDeCanto(true, true)} />
        <View style={bracoDeCanto(true, false)} />
        <View style={bracoDeCanto(false, true)} />
        <View style={bracoDeCanto(false, false)} />

        <View
          style={{
            position: 'absolute',
            left: esquerda,
            width: lado,
            top: topo + lado / 2,
            height: 2,
            backgroundColor: t.primaria,
            opacity: 0.7,
          }}
        />
      </>
    );
  }

  const temCamara = !MODO_TERMINAL && camera.pronta;

  /** O LED só existe onde a câmera diz que tem — Chrome no Android, hoje. */
  const temLanterna = !MODO_TERMINAL && camera.temLanterna;

  /**
   * A tela do computador, com leitor USB — o totem do Gertec no app.
   *
   * Sem câmera não há o que mostrar por baixo, e o vazio preto faria o operador
   * achar que o aparelho travou. Então o fundo vira instrução: o alvo grande diz
   * onde encostar o ingresso, e é a única coisa na tela enquanto ninguém passa.
   *
   * O desfecho continua sendo o mesmo de sempre — tela cheia com cor, ícone e
   * palavra, ou a faixa do mãos livres. Isto aqui é só o repouso.
   */
  function totem() {
    return (
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          gap: 22,
        }}>
        <View
          style={{
            width: 148,
            height: 148,
            borderRadius: 34,
            borderWidth: 3,
            borderColor: t.primaria,
            backgroundColor: t.superficie,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <ScanLine size={78} color={t.primaria} strokeWidth={1.8} />
        </View>

        <Text
          style={{
            color: t.texto,
            fontSize: 27,
            lineHeight: 32,
            fontFamily: fontes.extra,
            textAlign: 'center',
            letterSpacing: -0.5,
          }}>
          {txt('Aproxime o ingresso')}
        </Text>
        <Text
          style={{
            color: t.textoFraco,
            fontSize: 16,
            fontFamily: fontes.semi,
            textAlign: 'center',
          }}>
          {txt('Passe o código no leitor')}
        </Text>
      </View>
    );
  }

  /** Placa escura sobre a imagem da câmera. */
  const vidro = {
    backgroundColor: t.vidroFundo,
    borderWidth: 1,
    borderColor: t.vidroBorda,
    borderRadius: 12,
  };

  /**
   * Acende o LED da câmera. Só aparece onde ele existe: no navegador isso é o
   * Chrome no Android; no iPhone e no computador o botão some em vez de não
   * fazer nada.
   */
  function botaoLanterna() {
    if (!temLanterna) {
      return null;
    }
    return (
      <Pressable
        onPress={() => setLanterna(l => !l)}
        accessibilityLabel={lanterna ? txt('Desligar a lanterna') : txt('Ligar a lanterna')}
        style={{
          width: 52,
          height: 56,
          borderRadius: 15,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: lanterna ? t.primaria : t.superficie,
          borderWidth: 1,
          borderColor: lanterna ? t.primaria : t.borda,
        }}>
        <Flashlight
          size={21}
          color={lanterna ? t.tintaPrimaria : t.textoMedio}
          strokeWidth={2.2}
          fill={lanterna ? t.tintaPrimaria : 'transparent'}
        />
      </Pressable>
    );
  }

  /**
   * A faixa do desfecho no mãos livres.
   *
   * Cor, ícone e mensagem saem do `DESFECHO`, como na tela cheia — é o mesmo
   * contrato, só que ocupando o topo em vez da tela inteira, para a câmera
   * continuar lendo por baixo.
   */
  function faixaDoDesfecho(f: ResultadoValidacao) {
    const {setor, lote, numero} = identificacao(f);
    const detalhe = [
      f.motivo === 'JA_VALIDADO' && f.validadoEm
        ? txt('Entrou às {hora}', {hora: horaDe(f.validadoEm.dataHora)})
        : null,
      setor,
      numero ? txt('nº {numero}', {numero}) : null,
      lote ? txt('Lote {lote}', {lote}) : null,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <View style={{borderRadius: 16, backgroundColor: f.cor, padding: 16, gap: 12}}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 14}}>
          <View
            style={{
              width: 46,
              height: 46,
              borderRadius: 23,
              backgroundColor: `${f.tinta}26`,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <IconeDoDesfecho
              nome={f.icone}
              tamanho={26}
              cor={f.tinta}
              espessura={f.valido ? 3.2 : 2.4}
            />
          </View>

          <View style={{flex: 1, gap: 2}}>
            <Text
              numberOfLines={2}
              style={{
                color: f.tinta,
                fontSize: 21,
                fontFamily: fontes.extra,
                letterSpacing: -0.4,
              }}>
              {txtDoDominio(f.mensagem)}
            </Text>
            {detalhe ? (
              <Text
                numberOfLines={1}
                style={{color: `${f.tinta}CC`, fontSize: 13, fontFamily: fontes.semi}}>
                {detalhe}
              </Text>
            ) : null}
          </View>
        </View>

        {/* A barra é o tempo restante da faixa. Só na recusa: a do aprovado
            some antes de alguém chegar a olhar para ela. */}
        {!f.valido ? (
          <View
            style={{
              height: 5,
              borderRadius: 5,
              backgroundColor: `${f.tinta}33`,
              overflow: 'hidden',
            }}>
            <Animated.View
              style={{
                height: 5,
                borderRadius: 5,
                backgroundColor: f.tinta,
                width: faixaProgresso.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0%', '100%'],
                }),
              }}
            />
          </View>
        ) : null}
      </View>
    );
  }

  /** Estado de descanso do modo: o mínimo para o operador saber que está ligado. */
  function pilulaDeEstado() {
    return (
      <Pressable
        onPress={abrirHistorico}
        accessibilityLabel={txt('Ver ingressos validados neste aparelho')}
        style={[
          vidro,
          {paddingHorizontal: 14, paddingVertical: 12, flexDirection: 'row', alignItems: 'center', gap: 12},
        ]}>
        <View style={{width: 10, height: 10, borderRadius: 5, backgroundColor: t.destaque}} />

        <View style={{flex: 1}}>
          <Text style={{color: t.vidroTexto, fontSize: 15, fontFamily: fontes.extra}}>
            {txt('Mãos livres ligado')}
          </Text>
          <Text style={{color: t.vidroTextoFraco, fontSize: 12, fontFamily: fontes.regular}}>
            {txt('Aponte para o ingresso — sem toque')}
          </Text>
        </View>

        {/* Fila de envio à vista: sem rede a portaria não para, mas o operador
            precisa saber que ainda há coisa para subir antes de fechar. */}
        {resumo && resumo.pendentes > 0 ? (
          <View
            style={{
              paddingHorizontal: 9,
              paddingVertical: 4,
              borderRadius: 8,
              backgroundColor: t.avisoFundo,
              borderWidth: 1,
              borderColor: t.avisoBorda,
            }}>
            <Text
              style={{
                color: t.avisoTinta,
                fontSize: 10,
                fontFamily: fontes.negrito,
                letterSpacing: 0.7,
                textTransform: 'uppercase',
              }}>
              {txt('{quantas} por enviar', {quantas: comMilhar(resumo.pendentes)})}
            </Text>
          </View>
        ) : null}

        <Text style={[estilos.numero, {color: t.vidroTexto, fontSize: 26, lineHeight: 28}]}>
          {comMilhar(resumo?.nestaMaquina ?? 0)}
        </Text>
      </Pressable>
    );
  }

  return (
    <View style={[estilos.tela, {backgroundColor: t.fundo}]}>
      {MODO_TERMINAL ? (
        totem()
      ) : cameraLigada && naFrente ? (
        /* Fora da frente o leitor desmonta, mas o fluxo fica guardado por
           uns minutos (ver camera.tsx): no iPhone, soltar e pedir de novo é
           mais uma pergunta de permissão. */
        <LeitorCamera
          ativo={camaraAtiva}
          lanterna={lanterna}
          aoLer={receberCodigo}
          aoMudar={setCamera}
        />
      ) : null}

      {/* Mira é coisa de câmera: no terminal não há imagem para enquadrar. */}
      {temCamara ? mira() : null}

      {/* Moldura na cor do desfecho. No mãos livres o operador está olhando a
          fila, não a tela: a borda inteira mudando de cor é o que ele capta
          pelo canto do olho enquanto o som confirma. */}
      {faixa ? (
        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            borderWidth: 6,
            borderColor: faixa.cor,
          }}
        />
      ) : null}

      <SafeAreaView style={{flex: 1}} edges={['top', 'bottom']}>
        {maosLivres ? (
          <View style={{padding: 16, paddingBottom: 0, gap: 10}}>
            {faixa ? faixaDoDesfecho(faixa) : pilulaDeEstado()}

            {/* Recusa: enquanto a leitura está travada, dizer por quê. Sem
                isso o operador acha que a câmera parou de funcionar. */}
            {faixa && !faixa.valido ? (
              <View
                style={[
                  vidro,
                  {flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 12},
                ]}>
                <Info size={17} color={t.vidroTextoFraco} strokeWidth={2.2} />
                <Text
                  style={{color: t.vidroTextoFraco, fontSize: 13, fontFamily: fontes.semi, flex: 1}}>
                  {txt('Leitura pausada — barre o portador antes do próximo')}
                </Text>
              </View>
            ) : null}

            {/* Aprovado: o contador sobe junto, é a confirmação de que entrou. */}
            {faixa?.valido ? pilulaDeEstado() : null}
          </View>
        ) : (
        <View style={{padding: 16, paddingBottom: 0, gap: 10}}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 10}}>
            <Pressable
              onPress={voltarAosSetores}
              hitSlop={6}
              accessibilityLabel={txt('Voltar aos setores')}
              style={[vidro, {width: 44, height: 44, borderRadius: 13, alignItems: 'center', justifyContent: 'center'}]}>
              <ArrowLeft size={20} color={t.vidroTexto} strokeWidth={2.2} />
            </Pressable>

            <View style={[vidro, {flex: 1, paddingHorizontal: 12, paddingVertical: 4}]}>
              <Text
                numberOfLines={1}
                style={{
                  color: t.vidroTexto,
                  fontSize: 14,
                  fontFamily: fontes.negrito,
                  letterSpacing: -0.2,
                }}>
                {eventoNome}
              </Text>
              <Text style={{color: t.destaque, fontSize: 12, fontFamily: fontes.semi}}>
                {caixa?.nome ?? ''}
              </Text>
            </View>
          </View>

          <View style={{flexDirection: 'row', gap: 8}}>
            {/* O contador é o botão do histórico: ele já diz quantos entraram
                por aqui, e tocar nele mostra quais foram. Um quarto botão na
                barra de baixo espremeria Digitar e Buscar, que são os que o
                operador usa com a fila andando. */}
            <Pressable
              onPress={abrirHistorico}
              accessibilityLabel={txt('Ver ingressos validados neste aparelho')}
              style={[
                vidro,
                {flex: 1, paddingHorizontal: 14, paddingVertical: 12, flexDirection: 'row', alignItems: 'baseline', gap: 9},
              ]}>
              <Text style={[estilos.numero, {color: t.vidroTexto, fontSize: 30, lineHeight: 32}]}>
                {comMilhar(resumo?.nestaMaquina ?? 0)}
              </Text>
              <Text
                style={{
                  color: t.vidroTextoFraco,
                  fontSize: 11,
                  fontFamily: fontes.regular,
                  letterSpacing: 0.7,
                  textTransform: 'uppercase',
                  flex: 1,
                }}>
                {txt('validados neste aparelho')}
              </Text>
              <ChevronRight
                size={18}
                color={t.vidroTextoFraco}
                strokeWidth={2.2}
                style={{alignSelf: 'center'}}
              />
            </Pressable>

            {resumo && resumo.pendentes > 0 ? (
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 12,
                  borderRadius: 12,
                  backgroundColor: t.avisoFundo,
                  borderWidth: 1,
                  borderColor: t.avisoBorda,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 7,
                }}>
                <Text style={[estilos.numero, {color: t.avisoTinta, fontSize: 18}]}>
                  {resumo.pendentes}
                </Text>
                <Text
                  style={{
                    color: t.avisoTinta,
                    fontSize: 10,
                    fontFamily: fontes.regular,
                    letterSpacing: 0.7,
                    textTransform: 'uppercase',
                    lineHeight: 12,
                  }}>
                  {'por\nenviar'}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
        )}

        {/* Câmera desligada: o meio da tela diz por que não há imagem e liga
            de novo com um toque. Fica nesta camada, e não atrás com a imagem
            da câmera, porque esta camada cobre a tela inteira e engoliria o
            toque. */}
        {podeDesligarCamera && !cameraLigada ? (
          <Pressable
            onPress={() => setCameraLigada(true)}
            accessibilityLabel={txt('Ligar a câmera')}
            style={{flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, gap: 18}}>
            <View
              style={{
                width: 120,
                height: 120,
                borderRadius: 30,
                borderWidth: 2,
                borderColor: t.borda,
                backgroundColor: t.superficie,
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <CameraOff size={56} color={t.textoFraco} strokeWidth={1.8} />
            </View>
            <Text
              style={{
                color: t.texto,
                fontSize: 24,
                lineHeight: 29,
                fontFamily: fontes.extra,
                textAlign: 'center',
                letterSpacing: -0.4,
              }}>
              {txt('Câmera desligada')}
            </Text>
            <Text style={{color: t.textoFraco, fontSize: 15, fontFamily: fontes.semi, textAlign: 'center'}}>
              {txt('Toque para ligar.')}
            </Text>
          </Pressable>
        ) : (
          <View style={{flex: 1}} />
        )}

        {maosLivres ? (
          /* Interface mínima, como manda a especificação: um jeito de sair e
             mais nada. Digitar e buscar ficam de fora — menos caminhos, menos
             toque acidental com o aparelho apontado para a fila. A lanterna
             fica: mãos livres é modo de portaria cheia, que costuma ser à
             noite, e sem luz a câmera não lê. */
          <View style={{padding: 16, flexDirection: 'row', gap: 10}}>
            <Pressable
              onLongPress={() => ligarMaosLivres(false)}
              delayLongPress={TOQUE_PARA_SAIR_MS}
              accessibilityLabel={txt('Sair do mãos livres: mantenha pressionado')}
              style={[estilos.botao, {flex: 1, borderWidth: 2, borderColor: t.primaria}]}>
              <X size={20} color={t.primaria} strokeWidth={2.6} />
              <Text style={[estilos.textoBotao, {color: t.primaria, fontSize: 17}]}>
                {txt('Segure para sair')}
              </Text>
            </Pressable>

            {botaoLanterna()}
          </View>
        ) : (
          <View style={{padding: 16, gap: 10}}>
            {/* Liga e desliga a câmera: um botão redondo, só com o ícone do
                leitor, no centro da tela — no eixo da mira, e não da barra de
                baixo, que é torta por causa dos botões da direita. Ligada, o
                fundo é vidro sobre a imagem; desligada, a cor da marca chama o
                toque (o meio da tela explica o porquê). */}
            {podeDesligarCamera ? (
              <View style={{alignItems: 'center'}}>
                <Pressable
                  onPress={() => setCameraLigada(ligada => !ligada)}
                  accessibilityLabel={cameraLigada ? txt('Desligar a câmera') : txt('Ligar a câmera')}
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 32,
                    borderWidth: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: cameraLigada ? t.vidroFundo : t.primaria,
                    borderColor: cameraLigada ? t.vidroBorda : t.primaria,
                  }}>
                  <IconeLeitor tamanho={34} cor={cameraLigada ? t.vidroTexto : t.tintaPrimaria} />
                </Pressable>
              </View>
            ) : null}

            <View style={{flexDirection: 'row', gap: 10}}>
              <Pressable
                onPress={() => setDigitando(true)}
                style={[
                  estilos.botaoSecundario,
                  {flex: 1, backgroundColor: t.superficie, borderColor: t.borda},
                ]}>
                <Keyboard size={19} color={t.textoMedio} strokeWidth={2.2} />
                <Text style={[estilos.textoBotaoSecundario, {color: t.textoMedio, fontSize: 15}]}>
                  {txt('Digitar')}
                </Text>
              </Pressable>

              <Pressable
                onPress={() => navigation.navigate('Ingressos', {eventoId, eventoNome})}
                style={[
                  estilos.botaoSecundario,
                  {flex: 1, backgroundColor: t.superficie, borderColor: t.borda},
                ]}>
                <Search size={19} color={t.textoMedio} strokeWidth={2.2} />
                <Text style={[estilos.textoBotaoSecundario, {color: t.textoMedio, fontSize: 15}]}>
                  {txt('Buscar')}
                </Text>
              </Pressable>

              {/* O raio liga o mãos livres — a lanterna herdou ícone próprio. */}
              <Pressable
                onPress={() => setPedindoMaosLivres(true)}
                accessibilityLabel={txt('Ligar o modo mãos livres')}
                style={{
                  width: 52,
                  height: 56,
                  borderRadius: 15,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: t.superficie,
                  borderWidth: 1,
                  borderColor: t.borda,
                }}>
                <Zap size={21} color={t.textoMedio} strokeWidth={2.2} />
              </Pressable>

              {botaoLanterna()}
            </View>
          </View>
        )}
      </SafeAreaView>

      <ModalDigitar
        visivel={digitando}
        valor={digitado}
        aoMudar={setDigitado}
        aoFechar={() => {
          setDigitando(false);
          setDigitado('');
        }}
        aoValidar={() => {
          const codigo = digitado;
          setDigitando(false);
          setDigitado('');
          validar(codigo);
        }}
      />

      <ModalMaosLivres
        visivel={pedindoMaosLivres}
        aoFechar={() => setPedindoMaosLivres(false)}
        aoLigar={() => ligarMaosLivres(true)}
      />

      <ModalHistorico
        visivel={vendoHistorico}
        entradas={historico}
        carregando={carregandoHistorico}
        temMais={temMaisHistorico}
        aoCarregarMais={() => carregarHistorico(historico.length)}
        aoFechar={() => setVendoHistorico(false)}
      />

      <Modal visible={resultado !== null} transparent={false} animationType="fade">
        {resultado && confirmando ? (
          <TelaConfirmarDados
            // Remonta a cada ingresso: é o que reinicia a contagem de dez
            // segundos em vez de o segundo nominal herdar o relógio do primeiro.
            key={resultado.codbar}
            resultado={resultado}
            // No mãos livres o nominal é justamente o caso que a cadeia manda
            // um humano decidir — liberar sozinho ali esvaziaria o único ponto
            // de conferência que sobrou no modo.
            autoLiberar={!maosLivres}
            processando={processando}
            aoCancelar={() => setResultado(null)}
            aoLiberar={() => {
              const codigo = resultado.codbar;
              setResultado(null);
              validar(codigo, true);
            }}
          />
        ) : resultado ? (
          <TelaDesfecho
            resultado={resultado}
            progresso={contagemRegressiva}
            aoContinuar={() => setResultado(null)}
          />
        ) : null}
      </Modal>
    </View>
  );
}

/** Digitação do código quando o papel não lê. */
function ModalDigitar({
  visivel,
  valor,
  aoMudar,
  aoFechar,
  aoValidar,
}: {
  visivel: boolean;
  valor: string;
  aoMudar: (v: string) => void;
  aoFechar: () => void;
  aoValidar: () => void;
}) {
  const t = useTema();
  useIdioma();

  return (
    <Modal visible={visivel} transparent animationType="slide" onRequestClose={aoFechar}>
      <View
        style={{
          flex: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          backgroundColor: t.escurecedorModal,
        }}>
        {/* No computador a folha acompanha a coluna das telas, em vez de
            atravessar a janela inteira. */}
        <SafeAreaView
          edges={['bottom']}
          style={{
            width: '100%',
            maxWidth: LARGURA_DA_COLUNA,
            backgroundColor: t.fundo,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
          }}>
          <View style={{padding: 20, gap: 16}}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
              <Text style={[estilos.tituloCabecalho, {color: t.texto, flex: 1, fontSize: 18}]}>
                {txt('Digitar código')}
              </Text>
              <Pressable
                onPress={aoFechar}
                hitSlop={8}
                accessibilityLabel="Fechar"
                style={[estilos.botaoIcone, {borderColor: t.borda}]}>
                <X size={20} color={t.textoFraco} strokeWidth={2.2} />
              </Pressable>
            </View>

            <View
              style={{
                backgroundColor: t.superficie,
                borderWidth: 2,
                borderColor: t.primaria,
                borderRadius: 14,
                paddingHorizontal: 18,
                paddingVertical: 16,
              }}>
              <Text
                style={[
                  estilos.mono,
                  {color: valor ? t.texto : t.textoApagado, fontSize: 24},
                ]}>
                {valor || txt('Código do ingresso')}
              </Text>
            </View>

            <Teclado
              aoDigitar={d => aoMudar(valor + d)}
              aoApagar={() => aoMudar(valor.slice(0, -1))}
              aoLimpar={() => aoMudar('')}
              aoConfirmar={() => valor.length > 0 && aoValidar()}
            />

            <Pressable
              onPress={aoValidar}
              disabled={valor.length === 0}
              style={[
                estilos.botao,
                {backgroundColor: valor.length > 0 ? t.primaria : t.superficieAlta},
              ]}>
              <ScanLine
                size={20}
                color={valor.length > 0 ? t.tintaPrimaria : t.textoApagado}
                strokeWidth={2.6}
              />
              <Text
                style={[
                  estilos.textoBotao,
                  {color: valor.length > 0 ? t.tintaPrimaria : t.textoApagado},
                ]}>
                {txt('Validar')}
              </Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
}

/**
 * A pergunta antes de ligar o mãos livres.
 *
 * Existe porque o modo muda o contrato da tela: ela deixa de pedir toque e
 * passa a decidir sozinha. O operador precisa saber disso antes, não descobrir
 * quando o primeiro ingresso passar sem ele confirmar — daí a lista do que
 * muda, incluindo o que continua exigindo decisão dele.
 */
function ModalMaosLivres({
  visivel,
  aoFechar,
  aoLigar,
}: {
  visivel: boolean;
  aoFechar: () => void;
  aoLigar: () => void;
}) {
  const t = useTema();
  useIdioma();

  const item = (marca: string, cor: string, texto: string) => (
    <View style={{flexDirection: 'row', gap: 9}}>
      <Text style={{color: cor, fontSize: 13.5, fontFamily: fontes.negrito}}>{marca}</Text>
      <Text style={{color: t.textoMedio, fontSize: 13.5, fontFamily: fontes.regular, flex: 1, lineHeight: 20}}>
        {texto}
      </Text>
    </View>
  );

  return (
    <Modal visible={visivel} transparent animationType="slide" onRequestClose={aoFechar}>
      <View
        style={{
          flex: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          backgroundColor: t.escurecedorModal,
        }}>
        <SafeAreaView
          edges={['bottom']}
          style={{
            width: '100%',
            maxWidth: LARGURA_DA_COLUNA,
            backgroundColor: t.fundo,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
          }}>
          <View style={{padding: 20, gap: 16}}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
              <Zap size={22} color={t.primaria} strokeWidth={2.2} />
              <View style={{flex: 1}}>
                <Text style={[estilos.tituloCabecalho, {color: t.texto, fontSize: 18}]}>
                  {txt('Ligar mãos livres?')}
                </Text>
                <Text style={{color: t.textoFraco, fontSize: 13, fontFamily: fontes.regular}}>
                  {txt('A tela não vai mais pedir toque')}
                </Text>
              </View>
            </View>

            <View style={{gap: 7}}>
              {item('✓', t.destaque, txt('Som e vibração em todo desfecho'))}
              {item('✓', t.destaque, txt('Recusa trava a leitura por 2,5 s'))}
              {item('✓', t.destaque, txt('A tela fica acesa enquanto o modo estiver ligado'))}
              {item('!', t.aviso, txt('Ingresso nominal ainda exige toque'))}
              {item('!', t.aviso, txt('Confira o volume: sem som, o modo fica cego'))}
            </View>

            <View style={{flexDirection: 'row', gap: 10}}>
              <Pressable
                onPress={aoFechar}
                style={[estilos.botao, {flex: 1, borderWidth: 1, borderColor: t.borda}]}>
                <Text style={[estilos.textoBotao, {color: t.textoFraco, fontSize: 16}]}>
                  {txt('Agora não')}
                </Text>
              </Pressable>

              <Pressable
                onPress={aoLigar}
                style={[estilos.botao, {flex: 1.3, backgroundColor: t.primaria}]}>
                <Zap size={19} color={t.tintaPrimaria} strokeWidth={2.6} />
                <Text style={[estilos.textoBotao, {color: t.tintaPrimaria}]}>{txt('Ligar')}</Text>
              </Pressable>
            </View>
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
}

/**
 * Histórico do acesso: as últimas entradas liberadas por este aparelho.
 *
 * Existe para a pergunta que chega na portaria depois do fato — "passou mesmo?",
 * "a que horas?", "esse aqui já entrou?". A primeira linha vem destacada porque
 * é quase sempre a que está sendo discutida: alguém acabou de passar e o
 * operador precisa confirmar o que a tela mostrou por dois segundos.
 *
 * Carrega de dez em dez. A portaria de um evento grande tem milhares de linhas,
 * e o aparelho que rola essa lista é o mesmo que precisa continuar lendo código.
 */
function ModalHistorico({
  visivel,
  entradas,
  carregando,
  temMais,
  aoCarregarMais,
  aoFechar,
}: {
  visivel: boolean;
  entradas: EntradaDoAcesso[];
  carregando: boolean;
  temMais: boolean;
  aoCarregarMais: () => void;
  aoFechar: () => void;
}) {
  const t = useTema();
  useIdioma();

  return (
    <Modal visible={visivel} transparent animationType="slide" onRequestClose={aoFechar}>
      <View
        style={{
          flex: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          backgroundColor: t.escurecedorModal,
        }}>
        <SafeAreaView
          edges={['bottom']}
          style={{
            width: '100%',
            maxWidth: LARGURA_DA_COLUNA,
            backgroundColor: t.fundo,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '82%',
          }}>
          <View style={{padding: 20, paddingBottom: 12, flexDirection: 'row', alignItems: 'center', gap: 12}}>
            <Text style={[estilos.tituloCabecalho, {color: t.texto, flex: 1, fontSize: 18}]}>
              {txt('Validados neste aparelho')}
            </Text>
            <Pressable
              onPress={aoFechar}
              hitSlop={8}
              accessibilityLabel="Fechar"
              style={[estilos.botaoIcone, {borderColor: t.borda}]}>
              <X size={20} color={t.textoFraco} strokeWidth={2.2} />
            </Pressable>
          </View>

          <FlatList
            data={entradas}
            keyExtractor={item => String(item.id)}
            contentContainerStyle={{paddingHorizontal: 20, paddingBottom: 20, gap: 8}}
            ListEmptyComponent={
              carregando ? undefined : (
                <Text
                  style={[
                    estilos.subtitulo,
                    {color: t.textoFraco, textAlign: 'center', paddingVertical: 28},
                  ]}>
                  {txt('Nenhuma entrada liberada por este aparelho ainda.')}
                </Text>
              )
            }
            renderItem={({item, index}) => {
              const ultimo = index === 0;
              return (
                <View
                  style={[
                    estilos.cartao,
                    {
                      backgroundColor: ultimo ? t.superficieAlta : t.superficie,
                      borderColor: ultimo ? t.primaria : t.borda,
                      borderWidth: ultimo ? 2 : 1,
                      gap: 4,
                    },
                  ]}>
                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                    <Text style={[estilos.numero, {color: t.texto, fontSize: 17}]}>
                      {horaDe(item.dataHora)}
                    </Text>

                    {ultimo ? (
                      <Text
                        style={{
                          color: t.destaque,
                          fontSize: 10,
                          fontFamily: fontes.negrito,
                          letterSpacing: 1,
                          textTransform: 'uppercase',
                        }}>
                        {txt('Último')}
                      </Text>
                    ) : null}

                    <View style={{flex: 1}} />

                    {/* O operador precisa saber o que ainda não subiu: é o que
                        impede o fechamento limpo no fim do turno. */}
                    {!item.enviado ? (
                      <Text
                        style={{
                          color: t.aviso,
                          fontSize: 10,
                          fontFamily: fontes.negrito,
                          letterSpacing: 0.8,
                          textTransform: 'uppercase',
                        }}>
                        {txt('Por enviar')}
                      </Text>
                    ) : null}
                  </View>

                  <Text
                    numberOfLines={1}
                    style={{
                      color: item.nome ? t.texto : t.textoApagado,
                      fontSize: 15,
                      fontFamily: fontes.semi,
                    }}>
                    {item.nome ?? txt('Sem nome no cadastro')}
                  </Text>

                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                    <Text style={[estilos.mono, {color: t.textoFraco, fontSize: 12, flex: 1}]}>
                      {item.codbar}
                    </Text>
                    {/* Ingresso numerado carrega o lugar junto do setor —
                        "Mesa nº 2" é o que o operador repete para o cliente. */}
                    {item.setor || item.numero ? (
                      <View style={{flexDirection: 'row', alignItems: 'baseline', gap: 5}}>
                        {item.setor ? (
                          <Text
                            numberOfLines={1}
                            style={{color: t.textoFraco, fontSize: 12, fontFamily: fontes.regular}}>
                            {item.setor}
                          </Text>
                        ) : null}
                        {item.numero ? (
                          <Text
                            style={{
                              color: t.destaque,
                              fontSize: 13,
                              fontFamily: fontes.negrito,
                              fontVariant: ['tabular-nums'],
                            }}>
                            {txt('nº {numero}', {numero: item.numero})}
                          </Text>
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                </View>
              );
            }}
            ListFooterComponent={
              <View style={{paddingTop: 12, alignItems: 'center'}}>
                {carregando ? (
                  <ActivityIndicator color={t.primaria} />
                ) : temMais ? (
                  <Pressable
                    onPress={aoCarregarMais}
                    style={[
                      estilos.botaoSecundario,
                      {alignSelf: 'stretch', backgroundColor: t.superficie, borderColor: t.borda},
                    ]}>
                    <Text style={[estilos.textoBotaoSecundario, {color: t.textoMedio, fontSize: 15}]}>
                      {txt('Carregar mais {quantas}', {quantas: PAGINA_HISTORICO})}
                    </Text>
                  </Pressable>
                ) : entradas.length > 0 ? (
                  <Text
                    style={{
                      color: t.textoApagado,
                      fontSize: 12,
                      fontFamily: fontes.regular,
                    }}>
                    {txt('Fim da lista')}
                  </Text>
                ) : null}
              </View>
            }
          />

          {/* Para o suporte por telefone: a versão fica à mão sem sair da
              leitura nem fechar o caixa. */}
          <View style={{paddingTop: 10}}>
            <Versao />
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
}

/**
 * A tela de resultado. Cor, ícone e palavra vêm de `DESFECHO` — é contrato com
 * o operador, não decisão desta tela.
 */
function TelaDesfecho({
  resultado,
  progresso,
  aoContinuar,
}: {
  resultado: ResultadoValidacao;
  progresso: Animated.Value;
  aoContinuar: () => void;
}) {
  const insets = useSafeAreaInsets();
  useIdioma();
  const {cor, tinta} = resultado;
  const suave = `${tinta}24`;
  const meia = `${tinta}C0`;
  const {setor, lote, numero} = identificacao(resultado);

  return (
    <View style={{flex: 1, backgroundColor: cor}}>
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          paddingTop: insets.top + 32,
          gap: 24,
        }}>
        <View
          style={{
            width: 132,
            height: 132,
            borderRadius: 66,
            backgroundColor: suave,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <IconeDoDesfecho
            nome={resultado.icone}
            tamanho={76}
            cor={tinta}
            espessura={resultado.motivo === 'VALIDADO' ? 3 : 2.2}
          />
        </View>

        <View style={{alignItems: 'center', gap: 10}}>
          {/* Uma linha, encolhendo se precisar. A palavra do desfecho é
              contrato com o operador: ele reconhece a tela antes de ler, e
              "VALIDAD / O" partido em duas linhas quebra esse reconhecimento
              no exato momento em que a fila está esperando.

              O tamanho por contagem de letras não bastava: "VALIDADO" tem
              oito e caía no ramo grande, que não cabe em tela de 720 px —
              apareceu no Sunmi V2s. Ajustar o limiar só empurraria o
              problema para a próxima palavra ou a próxima largura. */}
          <Text
            numberOfLines={1}
            adjustsFontSizeToFit
            style={{
              color: tinta,
              fontFamily: fontes.preto,
              fontSize: resultado.palavra.length > 8 ? 44 : 56,
              lineHeight: resultado.palavra.length > 8 ? 48 : 60,
              letterSpacing: -1.6,
              textAlign: 'center',
            }}>
            {txtDoDominio(resultado.palavra)}
          </Text>
          <Text
            style={{color: tinta, fontSize: 19, fontFamily: fontes.negrito, textAlign: 'center'}}>
            {txtDoDominio(resultado.mensagem)}
          </Text>

          {/* Setor e lote de todo ingresso, nominal ou não: é a conferência
              visual de que a pessoa está na porta certa. */}
          {setor || lote ? (
            <View style={{flexDirection: 'row', gap: 8, flexWrap: 'wrap', justifyContent: 'center'}}>
              {setor ? (
                <View style={{paddingHorizontal: 12, paddingVertical: 6, borderRadius: 9, backgroundColor: suave}}>
                  <Text style={{color: tinta, fontSize: 16, fontFamily: fontes.negrito}}>
                    {setor}
                  </Text>
                </View>
              ) : null}
              {lote ? (
                <View style={{paddingHorizontal: 12, paddingVertical: 6, borderRadius: 9, backgroundColor: suave}}>
                  <Text style={{color: meia, fontSize: 16, fontFamily: fontes.semi}}>
                    {txt('Lote {lote}', {lote})}
                  </Text>
                </View>
              ) : null}
            </View>
          ) : null}

          {resultado.motivo === 'JA_VALIDADO' && resultado.validadoEm ? (
            <Text style={{color: meia, fontSize: 17, fontFamily: fontes.semi, textAlign: 'center'}}>
              {txt('Entrou às {hora}', {hora: horaDe(resultado.validadoEm.dataHora)})}
            </Text>
          ) : null}

          {resultado.cancelado ? (
            <Text style={{color: meia, fontSize: 15, fontFamily: fontes.regular, textAlign: 'center'}}>
              {txt('Cancelado em {data}', {data: resultado.cancelado.dataCancelamento})}
              {resultado.cancelado.motivoCancelamento
                ? ` — ${resultado.cancelado.motivoCancelamento}`
                : ''}
            </Text>
          ) : null}
        </View>

        {/* Camarote, mesa, cadeira. O operador lê este número em voz alta para
            encaminhar a pessoa, então ele é grande o bastante para ser lido de
            relance, com a fila andando e o aparelho na mão. */}
        {numero ? (
          <View
            style={{
              alignItems: 'center',
              paddingHorizontal: 26,
              paddingVertical: 12,
              borderRadius: 18,
              backgroundColor: suave,
            }}>
            <Text
              style={{
                color: meia,
                fontSize: 11,
                fontFamily: fontes.negrito,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
              }}>
              {txt('Número')}
            </Text>
            <Text
              style={{
                color: tinta,
                fontFamily: fontes.preto,
                fontSize: 46,
                lineHeight: 52,
                letterSpacing: -1,
                fontVariant: ['tabular-nums'],
              }}>
              {numero}
            </Text>
          </View>
        ) : null}

        <View style={{paddingHorizontal: 16, paddingVertical: 10, borderRadius: 10, backgroundColor: suave}}>
          <Text style={[estilos.mono, {color: tinta, fontSize: 16}]}>{resultado.codbar}</Text>
        </View>

        {resultado.consumacao && Number(resultado.consumacao) > 0 ? (
          <Text style={{color: tinta, fontSize: 18, fontFamily: fontes.negrito}}>
            {txt('Consumação: R$ {valor}', {valor: resultado.consumacao})}
          </Text>
        ) : null}

        {resultado.online && resultado.motivo === 'JA_VALIDADO' ? (
          <View style={{alignSelf: 'stretch', borderRadius: 14, padding: 18, backgroundColor: suave, gap: 4}}>
            <Text
              style={{
                color: meia,
                fontSize: 12,
                fontFamily: fontes.negrito,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
              }}>
              {txt('Portador')}
            </Text>
            <Text style={{color: tinta, fontSize: 19, fontFamily: fontes.negrito}}>
              {resultado.online.nome}
            </Text>
            <Text style={{color: meia, fontSize: 15, fontFamily: fontes.regular}}>
              {[
                resultado.online.cpf,
                resultado.online.pedidoId &&
                  txt('pedido {numero}', {numero: resultado.online.pedidoId}),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </View>
        ) : null}
      </View>

      <View style={{paddingHorizontal: 24, paddingBottom: insets.bottom + 26, gap: 14}}>
        {/* O botão só existe na recusa: ali o operador está resolvendo com a
            pessoa e quer voltar à fila quando terminar, não quando o relógio
            mandar. A barra corre nos dois casos — a recusa esquecida na tela
            também devolve a câmera, só que dez segundos depois. */}
        {resultado.exigeConfirmacao ? (
          <Pressable onPress={aoContinuar} style={[estilos.botao, {backgroundColor: tinta}]}>
            <Text style={[estilos.textoBotao, {color: cor}]}>{txt('Continuar lendo')}</Text>
          </Pressable>
        ) : null}

        <View style={{height: 6, borderRadius: 6, backgroundColor: suave, overflow: 'hidden'}}>
          <Animated.View
            style={{
              height: 6,
              borderRadius: 6,
              backgroundColor: tinta,
              width: progresso.interpolate({
                inputRange: [0, 1],
                outputRange: ['0%', '100%'],
              }),
            }}
          />
        </View>
        <Text style={{color: meia, fontSize: 14, fontFamily: fontes.semi, textAlign: 'center'}}>
          {/* No computador não há câmera: a tela volta ao totem do leitor. */}
          {MODO_TERMINAL ? txt('Voltando ao leitor') : txt('Voltando à câmera')}
        </Text>
      </View>
    </View>
  );
}

/**
 * Ingresso nominal: o operador confere o documento antes de liberar.
 *
 * É o único desfecho que espera decisão humana, e por isso não é uma tela
 * chapada de cor: os dados do portador precisam ser lidos, não reconhecidos de
 * longe. A faixa no topo carrega a cor e o ícone do desfecho.
 */
function TelaConfirmarDados({
  resultado,
  processando,
  autoLiberar,
  aoCancelar,
  aoLiberar,
}: {
  resultado: ResultadoValidacao;
  processando: boolean;
  /** false no mãos livres: ali esta tela é o único ponto de decisão humana. */
  autoLiberar: boolean;
  aoCancelar: () => void;
  aoLiberar: () => void;
}) {
  const t = useTema();
  useIdioma();
  // O <Modal> do RN abre uma hierarquia nativa própria e o SafeAreaView de
  // dentro dela mede zero: no iPhone o cabeçalho subia para debaixo da Dynamic
  // Island e os botões encostavam no indicador de home. O hook lê os insets da
  // raiz — o contexto atravessa o modal — então o recuo entra na mão.
  const insets = useSafeAreaInsets();
  const online = resultado.online;
  const {setor, lote, numero} = identificacao(resultado);

  const espera = resultado.timeoutMs;
  const restante = useRef(new Animated.Value(1)).current;
  const [segundos, setSegundos] = useState(Math.round(espera / 1000));
  // A função do pai é recriada a cada render; guardada em ref, a contagem não
  // reinicia junto.
  const liberar = useRef(aoLiberar);
  liberar.current = aoLiberar;

  /**
   * Sem toque nenhum, libera em dez segundos.
   *
   * A fila não pode parar porque o operador não tocou na tela. O preço é real:
   * a conferência do documento deixa de ser obrigatória e vira opcional — quem
   * se distrair libera o ingresso sem olhar. Por isso a contagem é visível e a
   * barra anda, em vez de o ingresso passar calado.
   *
   * Reinicia a cada ingresso porque o pai remonta esta tela pela `key` do
   * código; sem isso o segundo nominal da fila herdaria o relógio do primeiro.
   */
  useEffect(() => {
    if (!autoLiberar) {
      return;
    }

    Animated.timing(restante, {
      toValue: 0,
      duration: espera,
      useNativeDriver: false,
    }).start();

    const tique = setInterval(() => setSegundos(s => Math.max(0, s - 1)), 1000);
    const relogio = setTimeout(() => liberar.current(), espera);

    return () => {
      clearInterval(tique);
      clearTimeout(relogio);
    };
  }, [restante, espera, autoLiberar]);

  /**
   * Uma caixa de dado. O `flex: 1` só funciona dentro de uma linha: numa coluna
   * de altura automática ele vira `flexBasis: 0` sem folga para crescer, a caixa
   * encolhe até o padding e o texto some. Por isso todo uso solto vai embrulhado
   * em `linha()` — foi essa a caixa vazia que apareceu na conferência.
   */
  const dado = (rotulo: string, valor: string, grande = false, destaque = false) => (
    <View
      style={{
        flex: 1,
        borderWidth: destaque ? 2 : 1,
        borderColor: destaque ? t.primaria : t.borda,
        backgroundColor: t.superficie,
        borderRadius: 14,
        paddingHorizontal: 16,
        paddingVertical: 13,
        gap: 2,
      }}>
      <Text
        style={{
          color: destaque ? t.primaria : t.textoFraco,
          fontSize: 10,
          fontFamily: fontes.negrito,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}>
        {rotulo}
      </Text>
      <Text
        style={{
          color: t.texto,
          fontSize: destaque ? 26 : grande ? 19 : 15,
          fontFamily: destaque || grande ? fontes.negrito : fontes.semi,
          fontVariant: destaque || grande ? ['tabular-nums'] : undefined,
        }}>
        {valor || '—'}
      </Text>
    </View>
  );

  const linha = (...caixas: React.ReactNode[]) => (
    <View style={{flexDirection: 'row', gap: 12}}>{React.Children.toArray(caixas)}</View>
  );

  return (
    <View style={{flex: 1, backgroundColor: t.fundo}}>
      {/* A cor sangra até a borda de cima, atrás da barra de status; só o
          conteúdo desce. É o recuo que garante que o operador leia o desfecho
          inteiro antes de liberar alguém. */}
      <View
        style={{
          backgroundColor: resultado.cor,
          paddingHorizontal: 24,
          paddingTop: insets.top + 20,
          paddingBottom: 20,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 14,
        }}>
        <IconeDoDesfecho nome={resultado.icone} tamanho={30} cor={resultado.tinta} />
        <View style={{flex: 1, gap: 2}}>
          <Text
            style={{
              color: resultado.tinta,
              fontSize: 22,
              fontFamily: fontes.extra,
              letterSpacing: -0.4,
            }}>
            {txtDoDominio(resultado.mensagem)}
          </Text>
          <Text style={{color: `${resultado.tinta}CC`, fontSize: 14, fontFamily: fontes.semi}}>
            {autoLiberar
              ? txt('Confira o documento antes de liberar')
              : txt('Mãos livres pausado — precisa de você')}
          </Text>
        </View>
      </View>

      <View style={{flex: 1, padding: 24, gap: 18}}>
        <View style={{gap: 6}}>
          <Text style={[estilos.rotulo, {color: t.textoFraco, fontSize: 12}]}>{txt('Nome')}</Text>
          <Text style={[estilos.titulo, {color: t.texto, fontSize: 27, lineHeight: 31}]}>
            {online?.nome || txt('Sem nome')}
          </Text>
        </View>

        <View style={{gap: 12}}>
          {/* Setor e lote primeiro: são a conferência de que a pessoa está na
              porta certa, e existem mesmo sem cadastro online. */}
          {linha(dado(txt('Setor'), setor ?? ''), lote ? dado(txt('Lote'), lote) : null)}

          {/* O número do camarote ou da mesa é o que o operador fala para
              encaminhar a pessoa — daí o destaque. */}
          {numero ? linha(dado(txt('Número'), numero, false, true)) : null}

          {/* Campo sem conteúdo não vira caixa muda no meio da conferência. */}
          {online?.cpf || online?.pedidoId
            ? linha(
                online?.cpf ? dado(txt('CPF'), online.cpf, true) : null,
                online?.pedidoId ? dado(txt('Pedido'), online.pedidoId) : null,
              )
            : null}

          {online?.email ? linha(dado(txt('E-mail'), online.email)) : null}
        </View>

        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            paddingHorizontal: 16,
            paddingVertical: 14,
            borderRadius: 14,
            backgroundColor: t.avisoFundo,
            borderWidth: 1,
            borderColor: t.avisoBorda,
          }}>
          <IconeDoDesfecho nome="alertaTriangulo" tamanho={19} cor={t.avisoTinta} />
          <Text
            style={{
              color: t.avisoTinta,
              fontSize: 13,
              fontFamily: fontes.semi,
              lineHeight: 18,
              flex: 1,
            }}>
            {txt('Validado uma única vez. Não há como desfazer.')}
          </Text>
        </View>

        <Text style={[estilos.mono, {color: t.textoApagado, fontSize: 14}]}>
          {resultado.codbar}
        </Text>
      </View>

      <View style={{paddingHorizontal: 24, paddingBottom: insets.bottom + 26, gap: 12}}>
        {processando || !autoLiberar ? null : (
          <View style={{gap: 6}}>
            <View
              style={{
                height: 5,
                borderRadius: 5,
                backgroundColor: t.superficieAlta,
                overflow: 'hidden',
              }}>
              <Animated.View
                style={{
                  height: 5,
                  borderRadius: 5,
                  backgroundColor: t.primaria,
                  width: restante.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0%', '100%'],
                  }),
                }}
              />
            </View>
            <Text
              style={{
                color: t.textoFraco,
                fontSize: 13,
                fontFamily: fontes.semi,
                textAlign: 'center',
              }}>
              {txt('Libera sozinho em {segundos}s', {segundos})}
            </Text>
          </View>
        )}

        <View style={{flexDirection: 'row', gap: 12}}>
          <Pressable
            onPress={aoCancelar}
            style={[estilos.botao, {flex: 1, borderWidth: 1, borderColor: t.borda}]}>
            <Text style={[estilos.textoBotao, {color: t.textoFraco, fontSize: 17}]}>{txt('Cancelar')}</Text>
          </Pressable>

          <Pressable
            onPress={aoLiberar}
            disabled={processando}
            style={[estilos.botao, {flex: 1.4, backgroundColor: t.primaria}]}>
            {processando ? (
              <ActivityIndicator color={t.tintaPrimaria} />
            ) : (
              <>
                <IconeDoDesfecho nome="entrou" tamanho={20} cor={t.tintaPrimaria} espessura={3} />
                <Text style={[estilos.textoBotao, {color: t.tintaPrimaria}]}>{txt('Liberar')}</Text>
              </>
            )}
          </Pressable>
        </View>

        {/* Sem esta linha o operador não sabe se sair daqui devolve o modo ou
            se ele vai precisar ligar de novo com a fila esperando. */}
        {autoLiberar ? null : (
          <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8}}>
            <Zap size={15} color={t.textoApagado} strokeWidth={2.2} />
            <Text style={{color: t.textoApagado, fontSize: 13, fontFamily: fontes.regular}}>
              {txt('Volta ao mãos livres após decidir')}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}
